import React, {useCallback, useMemo, useState} from "react";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import {Col, Row} from "reactstrap";
import Divider from "@mui/material/Divider";
import Form from "../../base/form";
import Button from "../../base/button/button";
import {deepEqual} from "../../../../core/services/utils/utils";
import Dialog from "../../base/dialog";
import useIsMounted from "../../../hooks/use-is-mounted";
import {MuiRFF, Yup} from '../../form';
import Api from "../../../../core/services/api/api";

const formNames = {
    code: 'code',
    name: 'name',
    description: 'description',
}

const UpsertTerritoryDialog = ({open, close, data, onUpsert}) => {
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();

    const schema = useMemo(() => Yup.object().shape({
        [formNames.code]: Yup.string().trim().required(ValidateMessages.required)
            .max(10, ({max, value}) => `Name must be at most ${max} characters. ${(value)?.length ?? 0}/${max}`),
        [formNames.name]: Yup.string().trim().nullable().required(ValidateMessages.required),
        [formNames.description]: Yup.string().trim().nullable(),
    }), []);

    const validate = useMemo(() => makeValidate(schema), [schema]);
    const required = useMemo(() => makeRequired(schema), [schema]);

    const inEditMode = useMemo(() => !!data, [data]);

    const initialValues = useMemo(() => ({
        [formNames.code]: data?.code ?? undefined,
        [formNames.name]: data?.name ?? '',
        [formNames.description]: data?.description ?? '',
    }), [data?.code, data?.description, data?.name])

    /**
     * Updates the tex.
     * @type {(function(*): Promise<void>)}
     */
    const updateTerritory = useCallback(async (values) => {
        const forApi = {
            id: data?.id,
            code: values?.code,
            name: values?.name,
            description: values?.description,
        }
        if (forApi.code === data?.code && forApi.name === data?.name && forApi.description === data?.description) {
            return close();
        }
        const response = await Api.updateTerritory(forApi);
        if (!isMounted())
            return
        if (response?.resultFlag) {
            onUpsert({id: forApi.id, code: forApi.code, name: forApi.name, description: forApi.description})
        }
    }, [close, data?.code, data?.description, data?.id, data?.name, isMounted, onUpsert])

    /**
     * Creates a new Territory with the provided values.
     * @type {(function(*): Promise<void>)}
     */
    const createTerritory = useCallback(async (values) => {
        const forApi = {
            code: values?.code,
            name: values?.name,
            description: values?.description,
        }
        const response = await Api.createTerritory(forApi);
        if (!isMounted())
            return
        if (response?.resultFlag) {
            onUpsert({
                id: response.data?.id,
                code: response.data?.code,
                name: response.data?.name,
                description: response.data?.description,
            })
        }
    }, [isMounted, onUpsert])

    /**
     * creates a new driver or updates the [selectedDriver] by calling the api regarding this action
     */
    const onSubmit = useCallback(async (values) => {
        setSubmitting(true);
        let errors;
        if (!!inEditMode) {
            errors = await updateTerritory(values);
        } else {
            errors = await createTerritory(values);
        }
        if (!isMounted())
            return;
        setSubmitting(false);
        if (!errors) {
            close(false);
        }
        return errors;
    }, [inEditMode, isMounted, updateTerritory, createTerritory, close])

    return (
        <>
            <Dialog
                open={open}
                setOpen={close}
                maxWidth={'sm'}
            >
                <Row className='no-gutters align-items-center w-100 transition'>
                    <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                        <p className='text-lg text-color-1 font-weight-semi-bold text-center'>
                            {
                                inEditMode
                                    ? 'Edit Selected Territory'
                                    : 'Add a new Territory'
                            }
                        </p>
                    </Row>
                    <Divider className='divider'/>
                    <div className='my-3 w-100'/>
                    <Form
                        className='w-100'
                        onSubmit={onSubmit}
                        validate={validate}
                        initialValues={initialValues}
                        render={({initialValues: formInitialValues, values}) => {
                            return (
                                <Row className='no-gutters justify-content-around w-100'>
                                    <Row className='no-gutters justify-content-around w-85'>
                                        <Col xs={12} className={'mb-3'}>
                                            <MuiRFF.TextField
                                                label={'Code'}
                                                name={formNames.code}
                                                required={required[formNames.code]}
                                            />
                                        </Col>
                                        <Col xs={12} className={'mb-3'}>
                                            <MuiRFF.TextField
                                                label={'Name'}
                                                name={formNames.name}
                                                required={required[formNames.name]}
                                            />
                                        </Col>
                                        <Col xs={12} className={'mb-3'}>
                                            <MuiRFF.TextField
                                                label={'Description'}
                                                name={formNames.description}
                                                required={required[formNames.description]}
                                                multiline={true}
                                                minRows={5}
                                            />
                                        </Col>
                                    </Row>
                                    <div className='my-3 w-100'/>
                                    <Divider className='divider'/>
                                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                                        <Button
                                            disabled={!open || submitting}
                                            variant={'dialog'}
                                            color={'primary'}
                                            onClick={() => close()}
                                            borderRadius={'0 8px 0 8px'}
                                        >
                                            {submitting ? 'loading...' : 'Close'}
                                        </Button>
                                        <Button
                                            disabled={!open || submitting || (inEditMode && deepEqual(formInitialValues, values))}
                                            variant={'dialog'}
                                            color={'primary'}
                                            type={'submit'}
                                            borderRadius={'8px 0 8px 0'}
                                        >
                                            {submitting ? 'loading...' : inEditMode ? 'Update' : 'Add'}
                                        </Button>
                                    </Row>
                                </Row>
                            );
                        }}
                    />
                </Row>
            </Dialog>
        </>
    );
}

export default UpsertTerritoryDialog;
